import { API_URL } from './config';

const WEATHER_API_KEY = 'ee5ebbc30f9048b8a2d53211241904';

const parseJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const getErrorMessage = (response, payload) => {
  if (payload && payload.error) {
    return payload.error;
  }
  return `Request failed (${response.status})`;
};

const buildAuthenticatedUrl = (path, token) =>
  `${API_URL}${path}?token=${encodeURIComponent(token)}`;

const postForm = async (path, token, bodyObject) => {
  const body = new URLSearchParams(bodyObject);
  const response = await fetch(buildAuthenticatedUrl(path, token), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });

  const payload = await parseJsonSafe(response);
  if (!response.ok || (payload && payload.error)) {
    throw new Error(getErrorMessage(response, payload));
  }
  return payload || { message: 'OK' };
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const fetchUserSettings = async (token) => {
  const userInfoResponse = await fetch(buildAuthenticatedUrl('/userinfo', token));
  const userInfoPayload = await parseJsonSafe(userInfoResponse);
  if (!userInfoResponse.ok || (userInfoPayload && userInfoPayload.error)) {
    throw new Error(getErrorMessage(userInfoResponse, userInfoPayload));
  }

  let avatarPreviewUrl = null;
  const avatarResponse = await fetch(buildAuthenticatedUrl('/avatar', token));
  if (avatarResponse.ok) {
    const avatarBlob = await avatarResponse.blob();
    avatarPreviewUrl = await blobToDataUrl(avatarBlob);
  }

  return {
    userInfo: userInfoPayload || {},
    avatarPreviewUrl,
  };
};

export const searchCities = async (query) => {
  const cleanQuery = (query || '').trim();
  if (!cleanQuery) {
    return [];
  }

  const response = await fetch(
    `https://api.weatherapi.com/v1/search.json?key=${WEATHER_API_KEY}&q=${encodeURIComponent(cleanQuery)}`
  );
  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(response, payload));
  }

  return (Array.isArray(payload) ? payload : []).map((entry) => {
    const region = entry.region ? `${entry.region}, ` : '';
    return {
      id: `${entry.lat},${entry.lon}`,
      label: `${entry.name}, ${region}${entry.country}`,
      lat: entry.lat,
      lon: entry.lon,
      timezone: entry.tz_id || '',
    };
  });
};

export const saveTimezone = async (token, timezone) =>
  postForm('/saveTimezone', token, { timezone });

export const saveWeatherLocation = async (token, lat, lon) =>
  postForm('/saveWeatherLocation', token, { lat: String(lat), lon: String(lon) });

export const saveSessionLength = async (token, sessionLength) =>
  postForm('/saveSessionLength', token, { sessionLength: String(sessionLength) });

export const saveIntClocks = async (token, intClocks) =>
  postForm('/saveIntClocks', token, { data: JSON.stringify(intClocks) });

export const saveCelebrations = async (token, celebrations) =>
  postForm('/saveCelebrations', token, { data: JSON.stringify(celebrations) });

export const saveAvatar = async (token, avatarFile) => {
  const formData = new FormData();
  formData.append('avatar', avatarFile);

  const response = await fetch(buildAuthenticatedUrl('/saveAvatar', token), {
    method: 'POST',
    body: formData,
  });

  const payload = await parseJsonSafe(response);
  if (!response.ok || (payload && payload.error)) {
    throw new Error(getErrorMessage(response, payload));
  }

  return payload || { message: 'OK' };
};
